import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { match, P } from 'ts-pattern'

import { Icon } from '~/components/elements/Icon'
import { exists } from '~/utils'

import { DataSettingsCardSection } from './DataSettingsCardSection'
import styles from './DownloadFileSection.css'

const kilo = 1024
const toReadableFileSize = (size: number | undefined) =>
  match(size)
    .with(undefined, () => undefined)
    .with(P.number.lt(kilo), (size) => `${size}B`)
    .with(P.number.lt(kilo * kilo), (size) => `${(size / kilo).toFixed(2)}KB`)
    .otherwise((size) => `${(size / kilo / kilo).toFixed(2)}MB`)

type FileData = {
  filename: string | undefined
  size: number | undefined
}

export type DownloadFileSectionProps = {
  data: FileData | undefined
  onDownload: () => void
}

export const DownloadFileSection = ({
  data,
  onDownload,
}: DownloadFileSectionProps) => {
  const { t } = useTranslation()
  const handleDownloadButtonClick = React.useCallback<
    EH<'button', 'onClick'>
  >(() => {
    onDownload()
  }, [onDownload])

  const filename = data?.filename
  const size = data?.size

  if (!exists(filename)) {
    return null
  }

  return (
    <DataSettingsCardSection title={t('download_data')}>
      <div className="k-upload">
        <ul className={classNames('k-upload-files', styles.uploadFiles)}>
          <li className="k-file" tabIndex={-1}>
            <div className="k-file-single">
              <span className="k-file-icon-wrapper">
                <Icon
                  className="k-file-icon"
                  icon="file-data"
                  role="presentation"
                  size="1rem"
                />

                <span className="k-file-state" />
              </span>

              <span className="k-file-info">
                <span className="k-file-name" title={filename}>
                  {filename}
                </span>

                <span className="k-file-size">{toReadableFileSize(size)}</span>
              </span>

              <span className="k-upload-status">
                <button
                  aria-label="Download"
                  className="k-button k-button-md k-button-flat k-button-flat-base k-rounded-md k-icon-button k-icon-button k-upload-action"
                  tabIndex={-1}
                  title="Download"
                  type="button"
                  onClick={handleDownloadButtonClick}
                >
                  <Icon icon="download" role="presentation" size="1rem" />
                </button>
              </span>
            </div>
          </li>
        </ul>
      </div>
    </DataSettingsCardSection>
  )
}
